import { useEffect } from 'react';
import i18n from '@/lib/i18n/i18n';
import { toCleanCanonicalUrl } from '@/lib/seo/canonical-url';
import { resolveHreflangCluster, type HreflangCluster } from '@/lib/seo/hreflang-alternates';

export interface SEOData {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  city?: string;
}

const defaultImage = 'https://rakb.ma/og-image.png?v=2026-03';
const defaultUrl = 'https://rakb.ma';

const applyHreflangCluster = (cluster: HreflangCluster): void => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

  const updateHreflang = (hrefLang: string, href: string) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hrefLang);
    link.setAttribute('href', href);
    document.head.appendChild(link);
  };

  updateHreflang('fr', cluster.fr);
  if (cluster.en) {
    updateHreflang('en', cluster.en);
  }
  if (cluster.ar) {
    updateHreflang('ar', cluster.ar);
  }
  if (cluster.arMa) {
    updateHreflang('ar-MA', cluster.arMa);
  }
  updateHreflang('x-default', cluster.xDefault);
};

export const useSEO = (seoData: SEOData = {}) => {
  useEffect(() => {
    const {
      title,
      description,
      image,
      url,
      type = 'website',
      noindex = false,
      keywords,
      author = 'RAKB',
      publishedTime,
      modifiedTime,
      section,
      tags: _tags,
      city,
    } = seoData;

    const defaultTitle = i18n.t('seo.defaultTitle');
    const defaultDescription = i18n.t('seo.defaultDescription');
    const defaultKeywords = i18n.t('seo.defaultKeywords');

    let seoTitle = defaultTitle;
    if (title) {
      seoTitle = title.includes('RAKB') ? title : `${title} | RAKB`;
    }

    const seoDescription = description || defaultDescription;
    const seoImage = image || defaultImage;
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://rakb.ma';
    const seoUrlRaw = url || (typeof window !== 'undefined' ? window.location.href : defaultUrl);
    const seoUrlAbsolute = seoUrlRaw.startsWith('http')
      ? seoUrlRaw
      : `${baseOrigin}${seoUrlRaw.startsWith('/') ? '' : '/'}${seoUrlRaw}`;
    const seoUrl = toCleanCanonicalUrl(seoUrlAbsolute, baseOrigin);
    const seoKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

    document.title = seoTitle;

    const updateMetaTag = (attributeName: string, attributeValue: string, content: string) => {
      let meta = document.querySelector(`meta[${attributeName}="${attributeValue}"]`) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attributeName, attributeValue);
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
      return meta;
    };

    updateMetaTag('name', 'description', seoDescription);
    updateMetaTag('name', 'keywords', seoKeywords);
    updateMetaTag('name', 'author', author);

    updateMetaTag('name', 'geo.region', 'MA');
    if (city) {
      updateMetaTag('name', 'geo.placename', city);
    } else {
      updateMetaTag('name', 'geo.placename', 'Morocco');
      updateMetaTag('name', 'geo.position', '33.5731;-7.5898');
      updateMetaTag('name', 'ICBM', '33.5731, -7.5898');
    }

    if (noindex) {
      updateMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      updateMetaTag('name', 'robots', 'index, follow, max-image-preview:large');
    }

    updateMetaTag('property', 'og:type', type);
    updateMetaTag('property', 'og:url', seoUrl);
    updateMetaTag('property', 'og:title', seoTitle);
    updateMetaTag('property', 'og:description', seoDescription);
    updateMetaTag('property', 'og:image', seoImage);
    updateMetaTag('property', 'og:image:width', '1200');
    updateMetaTag('property', 'og:image:height', '630');

    const currentLang = i18n.language || 'fr';
    const localeMap: Record<string, string> = {
      fr: 'fr_FR',
      en: 'en_US',
      ar: 'ar_MA',
    };
    const ogLocale = localeMap[currentLang.split('-')[0]] || 'fr_FR';
    updateMetaTag('property', 'og:locale', ogLocale);

    updateMetaTag('property', 'og:site_name', 'RAKB');
    if (seoImage) {
      const existingOGImageAlt = document.querySelector('meta[property="og:image:alt"]');
      if (!existingOGImageAlt) {
        updateMetaTag('property', 'og:image:alt', seoDescription.substring(0, 420));
      }
    }

    if (type === 'article') {
      if (publishedTime) updateMetaTag('property', 'article:published_time', publishedTime);
      if (modifiedTime) updateMetaTag('property', 'article:modified_time', modifiedTime);
      if (section) updateMetaTag('property', 'article:section', section);
    }

    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:url', seoUrl);
    updateMetaTag('name', 'twitter:title', seoTitle);
    updateMetaTag('name', 'twitter:description', seoDescription);
    updateMetaTag('name', 'twitter:image', seoImage);
    if (seoImage) {
      const existingTwitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
      if (!existingTwitterImageAlt) {
        updateMetaTag('name', 'twitter:image:alt', seoDescription.substring(0, 420));
      }
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seoUrl);

    const hreflangCluster = resolveHreflangCluster(seoUrl);
    if (hreflangCluster) {
      applyHreflangCluster(hreflangCluster);
    } else {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', 'x-default');
      link.setAttribute('href', seoUrl);
      document.head.appendChild(link);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(seoData)]);
};
